import React, { useCallback, useMemo } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";

const safeId = (v) => (v == null ? null : Number(v));

const ReportPhotoPicker = ({ photos = [], selectedIds = [], onChangeSelected }) => {
  const selectedSet = useMemo(() => new Set((selectedIds || []).map(safeId)), [selectedIds]);

  const toggle = useCallback(
    (id) => {
      const pid = safeId(id);
      if (!pid) return;

      if (selectedSet.has(pid)) {
        onChangeSelected((selectedIds || []).filter((x) => safeId(x) !== pid));
      } else {
        // append to preserve selection order
        onChangeSelected([...(selectedIds || []), pid]);
      }
    },
    [selectedIds, onChangeSelected, selectedSet]
  );

  const move = useCallback(
    (pid, dir) => {
      const arr = (selectedIds || []).map(safeId).filter(Boolean);
      const idx = arr.indexOf(pid);
      if (idx < 0) return;

      const swapWith = dir === "up" ? idx - 1 : idx + 1;
      if (swapWith < 0 || swapWith >= arr.length) return;

      const next = [...arr];
      const tmp = next[idx];
      next[idx] = next[swapWith];
      next[swapWith] = tmp;
      onChangeSelected(next);
    },
    [selectedIds, onChangeSelected]
  );

  const selectedPhotos = useMemo(() => {
    const byId = new Map((photos || []).map((p) => [safeId(p.id), p]));
    return (selectedIds || [])
      .map(safeId)
      .filter(Boolean)
      .map((id) => byId.get(id))
      .filter(Boolean);
  }, [photos, selectedIds]);

  if (!photos || photos.length === 0) {
    return <div className="text-sm text-gray-500">No photos attached to this inspection.</div>;
  }

  return (
    <div className="space-y-4">
      {/* Selected preview/order list */}
      <div className="border rounded-md p-3 bg-gray-50">
        <div className="text-xs text-gray-600 mb-2">Selected order (PDF order)</div>
        {selectedPhotos.length === 0 ? (
          <div className="text-sm text-gray-500">No photos selected.</div>
        ) : (
          <div className="space-y-2">
            {selectedPhotos.map((p, idx) => {
              const pid = safeId(p.id);
              return (
                <div key={pid} className="flex items-center justify-between gap-3 bg-white border rounded-md p-2">
                  <div className="flex items-center gap-3">
                    <img
                      src={p.file_url || p.url || p.presigned_url || p.file_path || ""}
                      alt={p.caption || p.file_name || "photo"}
                      className="h-12 w-12 object-cover rounded"
                      onError={(e) => {
                        // avoid broken UI if no direct URL exists on UI side
                        e.currentTarget.style.display = "none";
                      }}
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {idx + 1}. {p.caption || p.file_name || `Photo #${pid}`}
                      </div>
                      <div className="text-xs text-gray-500">ID: {pid}</div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => move(pid, "up")}
                      className="p-2 rounded-md border bg-white hover:bg-gray-50"
                      title="Move up"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(pid, "down")}
                      className="p-2 rounded-md border bg-white hover:bg-gray-50"
                      title="Move down"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => toggle(pid)}
                      className="px-3 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 text-xs"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Available list (checkbox-style) */}
      <div className="border rounded-md overflow-hidden">
        <div className="px-3 py-2 bg-white border-b text-xs text-gray-600">Available photos</div>

        <div className="divide-y">
          {photos.map((p) => {
            const pid = safeId(p.id);
            const checked = selectedSet.has(pid);
            return (
              <label key={pid} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(pid)}
                />
                <div className="flex-1">
                  <div className="text-sm text-gray-900">{p.caption || p.file_name || `Photo #${pid}`}</div>
                  <div className="text-xs text-gray-500">ID: {pid}</div>
                </div>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ReportPhotoPicker);
